import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { ApiRequestError, api, apiFormData } from '@/lib/api';
import { courseSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { usePermissions } from '@/hooks/usePermissions';
import { courseBackUrl, courseTabHref } from '@/lib/course-admin-navigation';
import { CourseFormModern, type CourseFormValues } from './CourseFormModern';
import type { CourseFormTabId } from './course-form-constants';
import { fieldTabForElement } from './course-form-constants';
import { buildCourseFormData, courseToFormValues, flattenCategories } from './course-form-utils';

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

export function CourseEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);

  const [activeTab, setActiveTab] = useState<CourseFormTabId>('general');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [coverLabel, setCoverLabel] = useState('Subir miniatura');
  const [bannerLabel, setBannerLabel] = useState('Click para añadir banner');

  const { hasPermission } = usePermissions();

  const { data: contractTemplatesData } = useQuery({
    queryKey: ['contracts', 'templates'],
    queryFn: () => api<{ data: { id: string; title: string }[] }>('/contracts/templates'),
    enabled: hasPermission(PERMISSIONS.CONTRATOS_PLANTILLAS),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryNode[]>('/categories'),
  });

  const { data: formOptions } = useQuery({
    queryKey: ['courses', 'form-options'],
    queryFn: () =>
      api<{
        instructors: { id: string; fullName: string }[];
        supervisors: { id: string; fullName: string }[];
        roles: { id: string; label: string; userCount: number }[];
      }>(
        '/courses/form-options',
      ),
  });

  const { data: course, isLoading, error: courseError } = useQuery({
    queryKey: ['courses', id],
    queryFn: () => api<Record<string, unknown>>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const { register, handleSubmit, watch, setValue, reset } = useForm<CourseFormValues>({
    defaultValues: {
      name: '',
      code: '',
      slug: '',
      status: 'draft',
      shortDescription: '',
      fullDescription: '',
      categoryId: '',
      visibility: 'public',
      primaryInstructorId: '',
      additionalInstructors: [],
      supervisorUserIds: [],
      instructorRoleIds: [],
      isFree: true,
      price: '',
      requiresEnrollment: true,
      allowComments: true,
      showProgress: true,
      minCompliancePercent: '',
      academicHoursTime: '',
      startDate: '',
      endDate: '',
      maxEnrollment: '',
      metaTitle: '',
      metaDescription: '',
      contractTemplateId: '',
      requiresPrerequisiteCourse: false,
      prerequisiteCourseId: '',
      prerequisiteAssessmentId: '',
    },
  });

  useEffect(() => {
    if (!course) return;
    reset(courseToFormValues(course));
    if (course.coverImageUrl) setCoverLabel('Cambiar miniatura');
    if (course.bannerImageUrl) setBannerLabel('Cambiar banner');
  }, [course, reset]);

  const categoryId = (course?.category as { id?: string } | undefined)?.id;
  const showUrl = id ? courseTabHref(id, 'detalle', searchParams) : '/courses';
  const listBackUrl = courseBackUrl(searchParams, categoryId);

  const canEditCourse = hasPermission(PERMISSIONS.CURSOS_EDITAR);
  const canDeleteCourse = hasPermission(PERMISSIONS.CURSOS_ELIMINAR);
  const mutation = useMutation({
    mutationFn: async (values: CourseFormValues) => {
      await apiFormData(`/courses/${id}`, buildCourseFormData(values, coverFile, bannerFile), { method: 'PATCH' });
      if (canEditCourse && id) {
        await api(`/courses/${id}/supervisors`, { method: 'PUT', body: { userIds: values.supervisorUserIds } });
      }
    },
    onSuccess: (_, values) => {
      void queryClient.invalidateQueries({ queryKey: ['courses'] });
      if (id) void queryClient.invalidateQueries({ queryKey: ['courses', id] });
      if (categoryId) {
        void queryClient.invalidateQueries({ queryKey: ['courses', 'category', categoryId] });
      }
      if (values.categoryId) {
        void queryClient.invalidateQueries({ queryKey: ['courses', 'category', values.categoryId] });
      }
      toast(courseSuccessToast('edit'));
      navigate(showUrl);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : '';
      if (/curso previo|evaluación/i.test(message)) setActiveTab('requirements');
      toast(roleActionErrorToast(error, 'No se pudo guardar el curso'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/courses/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['courses'] });
      if (categoryId) {
        void queryClient.invalidateQueries({ queryKey: ['courses', 'category', categoryId] });
      }
      toast(courseSuccessToast('delete'));
      navigate(listBackUrl);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el curso'));
    },
  });

  const categoryOptions = flattenCategories(categories ?? []);
  const instructors = formOptions?.instructors ?? [];

  function handleInvalid(event: FormEvent<HTMLFormElement>) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('input, select, textarea')) return;
    event.preventDefault();
    setActiveTab(fieldTabForElement(target));
    window.requestAnimationFrame(() => {
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      ) {
        target.focus();
        target.reportValidity();
      }
    });
  }

  if (!isLoading && courseError instanceof ApiRequestError && courseError.status === 403) {
    return (
      <div className="lms-course-form lms-site-admin-hub-page font-sans">
        <div className="lms-course-form__shell">
          <p className="text-sm text-dash-on-surface-variant" role="alert">Sin acceso</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lms-course-form lms-site-admin-hub-page font-sans">
      <div className="lms-course-form__shell">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <HubBackLink to={showUrl} />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editar curso</h1>
              <p className="mt-1 text-dash-on-surface-variant">
                {course?.name ? `${course.name} — actualice los datos del curso` : 'Actualice los datos del curso'}
              </p>
            </div>
          </div>
          <Link to={showUrl} className="lms-course-form__btn lms-course-form__btn--cancel sm:shrink-0">
            <span className="material-symbols-outlined text-lg" aria-hidden="true">
              visibility
            </span>
            Ver curso
          </Link>
        </div>

        {isLoading ? (
          <p className="text-sm text-dash-on-surface-variant">Cargando curso…</p>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit((values) => {
              if (!canEditCourse) return;
              mutation.mutate(values);
            })}
            onInvalidCapture={handleInvalid}
            className="lms-course-form__layout"
          >
            <CourseFormModern
              register={register}
              watch={watch}
              setValue={setValue}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              categoryOptions={categoryOptions}
              instructors={instructors}
              supervisors={formOptions?.supervisors ?? []}
              roles={formOptions?.roles ?? []}
              contractTemplates={contractTemplatesData?.data ?? []}
              coverLabel={coverLabel}
              bannerLabel={bannerLabel}
              courseId={id}
              saveError={mutation.isError && mutation.error instanceof Error ? mutation.error.message : undefined}
              readOnly={!canEditCourse}
              onCoverChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setCoverFile(file);
                setCoverLabel(file ? file.name : 'Subir miniatura');
              }}
              onBannerChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setBannerFile(file);
                setBannerLabel(file ? file.name : 'Click para añadir banner');
              }}
            />

            <footer className="lms-course-form__footer lms-course-form__footer--split">
              {canDeleteCourse ? (
                <button
                  type="button"
                  className="lms-course-form__btn lms-course-form__btn--danger"
                  onClick={() => {
                    if (window.confirm('¿Eliminar el curso?')) deleteMutation.mutate();
                  }}
                >
                  Eliminar curso
                </button>
              ) : (
                <span />
              )}
              <div className="lms-course-form__footer-actions">
                <Link to={showUrl} className="lms-course-form__btn lms-course-form__btn--cancel">
                  Cancelar
                </Link>
                {canEditCourse && (
                  <button type="submit" className="lms-course-form__btn lms-course-form__btn--submit" disabled={mutation.isPending}>
                    Guardar cambios
                  </button>
                )}
              </div>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}
